import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as batshit from '@yornaath/batshit';
import {
  AgreementTemplateMutablePayload,
  createAgreementTemplate,
  createAgreementTemplateVersion,
  getAgreementTemplatePreview,
  getAgreementTemplatesByIds,
  getAgreementTemplateTokens,
  getAgreementTemplateVersions,
  getAllAgreementTemplates,
  publishAgreementTemplateVersion,
  updateAgreementTemplate,
  updateAgreementTemplateVersionContent,
} from '@uplift/api';
import sortBy from 'lodash/sortBy';
import { BaseQueryOpts } from '../baseTypes';


export const agreementTemplateQueryKeys = {
  all: [ 'agreementTemplates' ] as const,
  detail: (templateId: string) => [ 'agreementTemplates', templateId ] as const,
  versions: (templateId: string) => [ 'agreementTemplates', templateId, 'versions' ] as const,
  preview: (templateId: string, matchId?: string) => [ 'agreementTemplates', templateId, 'preview', matchId ] as const,
  tokens: [ 'agreementTemplates', 'tokens' ] as const,
};


export const useAllAgreementTemplatesQuery = () => {
  return useSuspenseQuery({
    queryKey: agreementTemplateQueryKeys.all,
    queryFn: async () => {
      const response = await getAllAgreementTemplates();

      return sortBy(response.data, 'updatedAt').reverse();
    },
  });
};


const agreementTemplateByIdBatch = batshit.create({
  fetcher: async (templateIds: string[]) => {
    const response = await getAgreementTemplatesByIds(templateIds.filter(Boolean));

    return response.data;
  },
  resolver: batshit.keyResolver('id'),
  scheduler: batshit.windowScheduler(10)
});


export const useAgreementTemplateQuery = (templateId: string) => {
  return useSuspenseQuery({
    queryKey: agreementTemplateQueryKeys.detail(templateId),
    queryFn: async () => {
      const response = await agreementTemplateByIdBatch.fetch(templateId);

      return response;
    },
  });
};


export const useAgreementTemplatePreviewQuery = (templateId: string, matchId?: string) => {
  return useSuspenseQuery({
    queryKey: agreementTemplateQueryKeys.preview(templateId, matchId),
    queryFn: async () => {
      const response = await getAgreementTemplatePreview(templateId, matchId);

      return response.data;
    },
  });
};


export const useAgreementTemplateVersionsQuery = (templateId: string) => {
  return useSuspenseQuery({
    queryKey: agreementTemplateQueryKeys.versions(templateId),
    queryFn: async () => {
      const response = await getAgreementTemplateVersions(templateId);

      return sortBy(response.data, 'version').reverse();
    },
  });
};


export const useAgreementTemplateTokensQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: agreementTemplateQueryKeys.tokens,
    queryFn: getAgreementTemplateTokens,
    select: (response) => response.data,
    ...opts,
  });
};


export const useCreateAgreementTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { name: string }) => createAgreementTemplate(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.all });
    },
  });
};


export const useUpdateAgreementTemplateMutation = (templateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AgreementTemplateMutablePayload) => updateAgreementTemplate(templateId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.detail(templateId) }),
      ]);
    },
  });
};


export const useUpdateAgreementTemplateVersionContentMutation = (templateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ version, content }: { version: number; content: string }) =>
      updateAgreementTemplateVersionContent(templateId, version, content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.versions(templateId) });
    },
  });
};


export const useCreateAgreementTemplateVersionMutation = (templateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ content }: { content: string }) =>
      createAgreementTemplateVersion(templateId, content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.versions(templateId) });
    },
  });
};


export const usePublishAgreementTemplateVersionMutation = (templateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { version: number }) => publishAgreementTemplateVersion(templateId, args.version),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.versions(templateId) }),
        queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.detail(templateId) }),
        queryClient.invalidateQueries({ queryKey: agreementTemplateQueryKeys.all }),
      ]);
    },
  });
};
